import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { Button } from '../../../components'
import { useMessage } from '../../../hooks'
import { storyService } from '../services'
import { resolveStatus } from '../helpers/status'
import config from '../../../config'
import { appName as APP_NAME } from '../appConfig'

// Hem IS the timeline. The nav tiles that used to live here are gone (the two views they
// pointed at are in the sidebar), and so is the separate /timeline page — this replaces it.
//
// The timeline itself is API-rendered HTML in an <iframe> (see
// api/apps/kayenta_stories/renderer/timeline.njk), not React, so the year filter can't be
// applied client-side over an array the way StoryList.js filters. It goes on the iframe's
// src as ?from=&to= and the renderer filters before it lays out the axis — which is also
// what makes the axis scale to the selected year instead of to all of history.

const CURRENT_YEAR = new Date().getFullYear()
const ALL_YEARS = ''   // the year filter's "no year selected" value; see the note below

const Home = () => {

  const appName = useOutletContext()
  const navigate = useNavigate()
  const { showMessage } = useMessage()

  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [frameHeight, setFrameHeight] = useState(null)

  // Flat string, not an object: the app's filter-reset convention is
  // `Object.values(filters).some(Boolean)` (see StoryList.js) and an object value would
  // always be truthy, so a nested {mode, from, to} would break it.
  const [year, setYear] = useState(String(CURRENT_YEAR))

  useEffect(() => {
    const ctrl = new AbortController()
    storyService.getAll(ctrl.signal)
      .then(res => { setStories(res.stories || []); setLoading(false) })
      .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda stories', 'error'); setLoading(false) } })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Years that actually have a dated story, so the stepper can't wander into empty
  // decades. Dates are 'YYYY-MM-DD' strings — slice, don't parse.
  const years = useMemo(() => {
    const set = new Set(
      stories
        .map(s => (s.story_data || {}).date)
        .filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d || ''))
        .map(d => d.slice(0, 4))
    )
    set.add(String(CURRENT_YEAR))   // always offer the current year, even in a quiet one
    return [...set].sort()
  }, [stories])

  const yearIndex = years.indexOf(year)
  const canStep = (delta) => year !== ALL_YEARS && yearIndex + delta >= 0 && yearIndex + delta < years.length
  const step = (delta) => { if (canStep(delta)) setYear(years[yearIndex + delta]) }

  const range = year === ALL_YEARS ? null : { from: `${year}-01-01`, to: `${year}-12-31` }

  // Counter for the subheading. Derived here rather than read back out of the iframe:
  // the same numbers the renderer computes, from data we already fetched.
  const counts = useMemo(() => {
    const inRange = stories.filter(s => {
      const d = (s.story_data || {}).date
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return false   // Idéer aren't on the timeline
      return !range || (d >= range.from && d <= range.to)
    })
    const by = { presented: 0, planned: 0, flagged: 0 }
    inRange.forEach(s => { const st = resolveStatus(s.story_data || {}); if (by[st] !== undefined) by[st]++ })
    return { total: inRange.length, ...by }
  }, [stories, year]) // eslint-disable-line react-hooks/exhaustive-deps

  const subtitle = () => {
    if (loading) return '…'
    const scope = year === ALL_YEARS ? 'totalt' : year
    if (!counts.total) return `Inga stories ${scope}`
    const parts = [`${counts.total} ${counts.total === 1 ? 'story' : 'stories'} ${scope}`]
    if (counts.presented) parts.push(`${counts.presented} genomförd${counts.presented > 1 ? 'a' : ''}`)
    if (counts.planned) parts.push(`${counts.planned} planerad${counts.planned > 1 ? 'e' : ''}`)
    if (counts.flagged) parts.push(`${counts.flagged} att bekräfta`)
    return parts.join(' · ')
  }

  const htmlUrl = `${config.api.url}/${APP_NAME}/story/timeline/html?embed=1`
    + (range ? `&from=${range.from}&to=${range.to}` : '')

  // ---- iframe messaging ----
  // Two channels, both from the API-served document into this page. The origin to check
  // against is the API's, NOT window.location.origin: the two are the same host in local
  // dev but different hosts in production, so comparing against our own origin passes
  // every local test and silently drops every message in production. This exact bug has
  // shipped once already in this app — see the root CLAUDE.md.
  const apiOrigin = useMemo(() => new URL(config.api.url).origin, [])
  const frameRef = useRef(null)

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.origin !== apiOrigin) return
      const msg = e.data
      if (!msg || !msg.type) return
      if (msg.type === 'kayenta-stories:navigate' && msg.slug) {
        navigate(`/${appName}/${msg.slug}`)
      } else if (msg.type === 'kayenta-stories:height' && msg.height) {
        setFrameHeight(msg.height)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [navigate, appName, apiOrigin])

  // A new src means a new document, whose height we haven't heard yet. Without this the
  // frame keeps the previous range's height until the new message lands — very visible
  // going from "all years" to a single quiet year.
  useEffect(() => { setFrameHeight(null) }, [htmlUrl])

  return (
    <div className="Home">
      <div className="home-primary ks-page ks-home">

        <div className="ks-page-header">
          <div>
            <h1>Tidslinje</h1>
            <div className="ks-page-subtitle">{subtitle()}</div>
          </div>
        </div>

        <div className="ks-toolbar">
          <Button
            value="+ ny story"
            className="button"
            onClick={() => navigate(`/${appName}/new`)} />

          <div className="ks-filter-bar">
            <div className="ks-year-picker">
              <button
                type="button"
                className="ks-year-arrow"
                onClick={() => step(-1)}
                disabled={!canStep(-1)}
                title="Föregående år"
                aria-label="Föregående år">‹</button>
              <span className="ks-year-val">{year === ALL_YEARS ? 'Alla år' : year}</span>
              <button
                type="button"
                className="ks-year-arrow"
                onClick={() => step(1)}
                disabled={!canStep(1)}
                title="Nästa år"
                aria-label="Nästa år">›</button>
              <span className="ks-year-divider"></span>
              <button
                type="button"
                className={'ks-year-all' + (year === ALL_YEARS ? ' active' : '')}
                onClick={() => setYear(year === ALL_YEARS ? String(CURRENT_YEAR) : ALL_YEARS)}
                aria-pressed={year === ALL_YEARS}>Alla år</button>
            </div>
          </div>
        </div>

        <iframe
          ref={frameRef}
          className="ks-timeline-frame"
          src={htmlUrl}
          title="Kayenta Stories — Tidslinje"
          style={frameHeight ? { height: frameHeight } : undefined} />

      </div>
    </div>
  )
}

export default Home
